import { getWidgetConfig } from '../runtime/widgetConfig'

const STOCK_IMPORT_TIMEOUT_MS = 30_000
const STOCK_PREVIEW_TIMEOUT_MS = 60_000

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    return await fetch(input, {
      ...init,
      signal: ctrl.signal,
    })
  } finally {
    clearTimeout(timer)
  }
}

export type AdobeStockImportPayload = {
  assetId: string
  title?: string | null
  filename?: string | null
  width?: number | null
  height?: number | null
  mime?: string | null
  previewUrl: string
}

function apiBase() {
  const raw = String((getWidgetConfig() as any)?.cartBackendBaseUrl || '').trim().replace(/\/+$/, '')
  if (!raw) return ''
  return raw
}

function apiRoot() {
  const base = apiBase()
  return base.replace(/\/v1$/, '')
}

function stockApiUrl(path = '') {
  const base = apiBase()
  const p = path ? (path.startsWith('/') ? path : `/${path}`) : ''
  return `${base}/stock${p}`
}

export async function fetchAdobeStockImportPayload(assetId: string): Promise<AdobeStockImportPayload> {
  const cleanAssetId = String(assetId || '').trim()
  if (!cleanAssetId) throw new Error('assetId missing')
  return fetchWithTimeout(stockApiUrl(`/import/${encodeURIComponent(cleanAssetId)}`), {}, STOCK_IMPORT_TIMEOUT_MS).then(async (res) => {
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(`Adobe-Stock-Import fehlgeschlagen (${res.status}): ${txt || res.statusText}`)
    }
    const payload = await res.json()
    if (payload?.previewUrl && /^\/(?!\/)/.test(String(payload.previewUrl))) {
      payload.previewUrl = `${apiRoot()}${String(payload.previewUrl)}`
    }
    return payload
  })
}

export async function fetchAdobeStockPreviewFile(assetId: string): Promise<{ file: File; payload: AdobeStockImportPayload }> {
  const payload = await fetchAdobeStockImportPayload(assetId)
  const response = await fetchWithTimeout(payload.previewUrl, {}, STOCK_PREVIEW_TIMEOUT_MS)
  if (!response.ok) {
    throw new Error(`Adobe-Stock-Vorschau konnte nicht geladen werden (${response.status})`)
  }

  const blob = await response.blob()
  const mime = String(payload.mime || blob.type || 'image/jpeg')
  const extension = mime.includes('png') ? 'png' : 'jpg'
  const safeBase = String(payload.filename || payload.title || `adobe-stock-${payload.assetId}`)
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || `adobe-stock-${payload.assetId}`
  const finalName = safeBase.includes('.') ? safeBase : `${safeBase}.${extension}`
  const file = new File([blob], finalName, { type: mime })

  return { file, payload }
}
